import { format, parseISO } from 'date-fns';

import { formatMoney } from '../shared/format';

import { JourneysPayload } from '@/services/lifecycleAPI';

export const formatCohortDate = (iso: string | null): string => {
  if (!iso) return 'Not set';
  const date = parseISO(iso);
  return Number.isNaN(date.getTime()) ? iso : format(date, 'MMM d, yyyy');
};

export const allowlistLabel = (global: JourneysPayload['global']): string => {
  if (!global.allowlist_enforced) return 'Not used here (everyone in the zips)';
  const count = global.recipient_allowlist.length;
  if (count === 0) return 'Nobody (allowlist is empty)';
  return `${count} ${count === 1 ? 'worker' : 'workers'}`;
};

export const audienceLine = (global: JourneysPayload['global']): string => {
  if (global.allowlist_enforced) {
    const count = global.recipient_allowlist.length;
    if (count === 0) return 'No messages go out: the allowlist is empty.';
    return `Messages go only to the ${count} allowlisted ${
      count === 1 ? 'worker' : 'workers'
    }.`;
  }
  const zips = global.cohort_zip_prefixes
    ? `workers in zip prefixes ${global.cohort_zip_prefixes}`
    : 'workers in any zip';
  const since = global.cohort_start_on
    ? ` who signed up on or after ${formatCohortDate(global.cohort_start_on)}`
    : '';
  return `Messages go to ${zips}${since}.`;
};

export const journeysOn = (journeys: JourneysPayload['journeys']): string => {
  const keys = journeys.filter((j) => j.enabled).map((j) => j.journey_key);
  return keys.length > 0 ? keys.join(', ') : 'none';
};

export const smsOffFor = (journeys: JourneysPayload['journeys']): string[] =>
  journeys.filter((j) => j.enabled && !j.sms_enabled).map((j) => j.journey_key);

// graphy (send_decision.rb budget_spent?) stops SMS once the month's spend is
// at or over the budget, so a $0 budget means SMS is off, and a spent budget
// has $0.00 left, never a negative amount.
export type BudgetState =
  | { kind: 'none' }
  | { kind: 'zero' }
  | { kind: 'spent'; budget: number; spend: number; overBy: number }
  | { kind: 'ok'; budget: number; spend: number; left: number; pct: number };

export const budgetState = (global: JourneysPayload['global']): BudgetState => {
  const budget = global.monthly_sms_budget_cents;
  const spend = global.spend_month_to_date_cents;
  if (budget === null) return { kind: 'none' };
  if (budget <= 0) return { kind: 'zero' };
  if (spend >= budget)
    return { kind: 'spent', budget, spend, overBy: spend - budget };
  return {
    kind: 'ok',
    budget,
    spend,
    left: budget - spend,
    pct: Math.min(100, Math.round((spend / budget) * 100)),
  };
};

// graphy prices an SMS at segments x sms_segment_cost_cents (Channels::Sms),
// so with no cost every SMS costs $0 and the monthly budget never stops a
// send (review RT-08). Only worth saying while a budget is set.
// A cost of 0 prices every SMS at $0 exactly as a blank one does, so the
// budget never stops a send either way (bug hunt 18).
export const segmentCostMissing = (global: JourneysPayload['global']) =>
  (global.sms_segment_cost_cents === null ||
    global.sms_segment_cost_cents <= 0) &&
  (global.monthly_sms_budget_cents ?? 0) > 0;

export const SEGMENT_COST_MISSING =
  'Segment cost not set: SMS spend is not counted';

export const budgetLine = (global: JourneysPayload['global']): string => {
  const state = budgetState(global);
  switch (state.kind) {
    case 'none':
      return 'SMS is off: no monthly budget is set.';
    case 'zero':
      return 'SMS is off: budget is $0.';
    case 'spent':
      return `SMS budget left this month: ${formatMoney(0)} (${
        state.overBy > 0
          ? `over budget by ${formatMoney(state.overBy)}`
          : 'used up'
      }), so SMS is not sent.`;
    default:
      return `SMS budget left this month: ${formatMoney(state.left)}.${
        segmentCostMissing(global)
          ? ' The SMS segment cost is not set, so SMS spend is not counted and the budget never stops SMS.'
          : ''
      }`;
  }
};
